export interface User {
  id: string;
  userName?: string;
  username?: string;
  fullName?: string;
  roles: string[];
  activated: boolean;
  createdAt: string;
  modifiedAt: string;
}

export interface Story {
  id: string;
  title: string;
  description: string;
  coverUrl?: string;
  status: 'draft' | 'published' | 'archived';
  categories: string[];
  gender: 'male' | 'female' | 'unisex';
  ongoing?: boolean;
  publishFrequency?: number | null;
  episodes?: Episode[];
  createdAt: string;
  modifiedAt: string;
  createdBy: string;
  modifiedBy: string;
}

export interface EpisodeSubtitleInfo {
  url: string;
  label: string;
}

// ── 260810: Phụ đề AI sinh từ audio của video (video upload từ ngoài, không có kịch bản) ──

export interface SubtitleJobEntry {
  text: string;
  startTime: number; // giây
  endTime: number;
}

/** Vòng đời: pending → running → awaiting_review → approved. failed ở bất kỳ đâu. */
export type SubtitleJobStatus =
  | 'pending'
  | 'running'
  | 'awaiting_review'
  | 'approved'
  | 'failed';

export interface SubtitleJob {
  id: string;
  episodeId: string;
  status: SubtitleJobStatus;
  language: string;
  sourceVideoUrl?: string | null;
  entries?: SubtitleJobEntry[] | null;
  entriesCount?: number | null;
  audioDuration?: number | string | null;
  model?: string | null;
  errorMessage?: string | null;
  /** Job xong nhưng đáng ngờ (phụ đề có thể cụt đuôi) — khác errorMessage, vẫn duyệt được. */
  warning?: string | null;
  attempt: number;
  vttUrl?: string | null;
  reviewedBy?: string | null;
  reviewedAt?: string | null;
  createdAt: string;
  modifiedAt: string;
}

export interface Episode {
  id: string;
  storyId: string;
  chap: number;
  thumbnailUrl?: string;
  videoUrl?: string;
  duration?: number;
  status: 'draft' | 'uploading' | 'processing' | 'ready' | 'scheduled' | 'published' | 'error' | 'failed';
  isLocked: boolean;
  unlockCondition?: string;
  // Epic 8: Multi-language subtitles { "en": { url, label }, "vi": { url, label } }
  subtitles?: Record<string, EpisodeSubtitleInfo>;
  publishedAt?: string | null;
  createdAt: string;
  modifiedAt: string;
  createdBy: string;
  modifiedBy: string;
}

export interface Tag {
  id: string;
  name: string;
  description?: string;
  status: 'active' | 'inactive';
  createdAt: string;
  modifiedAt: string;
  createdBy: string;
  modifiedBy: string;
}

export interface UploadStatus {
  id: string;
  episodeId: string;
  status: 'uploading' | 'processing' | 'completed' | 'failed';
  progress: number;
  error?: string;
}

export interface QueueStats {
  pending: number;
  processing: number;
  completed: number;
  failed: number;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  totalRecord: number;
}

export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  token: {
    accessToken: string;
    refreshToken: string;
  };
  user: {
    username: string;
    fullName: string;
    roles: string[];
  };
}

export interface SourceStory {
  id: string;
  name: string;
  author?: string;
  link?: string;
  summary?: string;
  pros?: string;
  cons?: string;
  hook?: string;
  caption?: string;
  imageStyle?: string;
  /** Bối cảnh/thời đại của truyện (260804) — nguồn chân lý cho mọi prompt sinh reference. */
  worldSetting?: import('./film').WorldSetting | null;
  createdAt: string;
  modifiedAt: string;
  createdBy?: string;
  modifiedBy?: string;
}

export interface StoryVoice {
  id: string;
  sourceStoryId: string;
  code: string;
  displayName: string;
  description?: string;
  gender: 'MALE' | 'FEMALE';
  provider: string;
  createdAt: string;
  modifiedAt: string;
  createdBy?: string;
  modifiedBy?: string;
}

export interface SourceChapter {
  id: string;
  sourceStoryId: string;
  orderIndex: number;
  name?: string;
  content?: string;
  link?: string;
  createdAt: string;
  modifiedAt: string;
  createdBy?: string;
  modifiedBy?: string;
}

export interface SourceProcess {
  id: string;
  sourceStoryId: string;
  name: string;
  orderIndex: number;
  chapterIds: string[];
  characterIds?: string[];
  status: 'DRAFT' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  summary?: string;
  totalScenes?: number;
  completedScenes?: number;
  isContentComplete?: boolean;
  createdAt: string;
  modifiedAt: string;
  createdBy?: string;
  modifiedBy?: string;
}

export interface Storyteller {
  id: string;
  sourceStoryId: string;
  name: string;
  personality: string;
  style: string;
  isActive: boolean;
  createdAt: string;
  modifiedAt: string;
  createdBy?: string;
  modifiedBy?: string;
}

export interface SourceCharacter {
  id: string;
  sourceStoryId: string;
  processId?: string;
  name: string;
  description?: string;
  role?: string;
  avatarUrl?: string;
  localizedData?: {
    name?: string;
    [key: string]: any;
  };
  aliases?: string[];
  appearanceOverride?: any;
  createdAt: string;
  modifiedAt: string;
  createdBy?: string;
  modifiedBy?: string;
}

// Epic 3: Character Checkpoint interface
export interface CharacterCheckpoint {
  id: string;
  characterId: string;
  sourceStoryId: string;
  effectiveFromPanelId: string;
  changeDescription: string;
  fullDescription: string;
  avatarUrl?: string;
  avatarStatus?: 'NOT_GENERATED' | 'GENERATING' | 'READY' | 'FAILED';
  klingElementId?: string;
  klingElementTaskId?: string;
  orderIndex: number;
  createdAt: string;
  modifiedAt: string;
  createdBy?: string;
  modifiedBy?: string;
}

// Epic 4: Scene Character Reference interface
export interface SceneCharacterReference {
  id: string;
  sceneId: string;
  characterId: string;
  sourceStoryId: string;
  outfitDescription?: string;
  referenceUrl?: string;
  isDefaultOutfit: boolean;
  sourceType: 'AI_PLANNED' | 'ADMIN_UPLOAD' | 'ADMIN_OVERRIDE';
  sourceId?: string;
  createdAt: string;
  modifiedAt: string;
  createdBy?: string;
  modifiedBy?: string;
}

export interface ExtractedCharacter {
  id?: string;
  name: string;
  description: string;
  role: string;
  isNew: boolean;
}

export interface SourceScene {
  id: string;
  sourceStoryId: string;
  processId: string;
  orderIndex: number;
  title: string;
  summary?: string;
  setting?: string;
  characterIds?: string[]; // UUID array
  reason?: string;
  keyMoments?: string[];
  endState?: string;
  totalPanels?: number;
  activePanels?: number;
  createdAt: string;
  modifiedAt: string;
  createdBy?: string;
  modifiedBy?: string;
}

export interface ExtractedScene {
  title: string;
  summary: string;
  setting: string;
  characterIds: string[]; // UUID array (AI returns directly)
  reason: string;
  keyMoments: string[];
  endState: string;
}

export interface SourcePanel {
  id: string;
  sourceStoryId: string;
  processId: string;
  sceneId: string;
  sceneOrderIndex: number;
  characterIds: string[];
  caption: string;
  localizedCaption?: string;
  visualDescription: string;
  refinedVisualDescription?: string;
  technicalPrompt?: string;
  visualPlan?: any;
  imagePath?: string;
  mediaType: 'IMAGE' | 'VIDEO';
  firstFrameUrl?: string;
  videoUrl?: string;
  videoDuration?: number;
  estimatedAudioDuration?: number;
  audioDuration?: number;
  motionPrompt?: string;
  multiShotConfig?: any; // JSONB field for storing AI shot config
  createdAt: string;
  modifiedAt: string;
  createdBy?: string;
  modifiedBy?: string;
}

export interface ExtractedPanel {
  caption: string;
  localizedCaption?: string;
  visualDescription: string;
  characterIds: string[];
  isVideo?: boolean;
  estimatedAudioDuration?: number;
}

export interface PromptTemplate {
  id: string;
  code: string;
  name: string;
  description?: string;
  template: string;
  variables?: any;
  expectedOutput?: string;
  isActive: boolean;
  createdAt: string;
  modifiedAt: string;
  createdBy?: string;
  modifiedBy?: string;
}

export interface SourceImageGeneration {
  id: string;
  ownerId: string;
  ownerType: 'CHARACTER' | 'PANEL';
  provider: 'GEMINI' | 'KLING';
  detail: {
    prompt: string;
    model?: string;
    aspectRatio?: string;
    [key: string]: any;
  };
  status: 'PENDING' | 'SUCCESS' | 'FAILED';
  error?: string;
  createdBy?: string;
  createdAt: string;
}

export interface SourceImage {
  id: string;
  ownerId: string;
  ownerType: 'CHARACTER' | 'PANEL';
  sourceType: 'AI_GENERATION' | 'UPLOAD' | 'URL';
  sourceId?: string;
  url: string;
  filename?: string;
  contentType?: string;
  width?: number;
  height?: number;
  size?: number;
  createdAt: string;
}

// 🆕 Epic 6: Subtitle Types
export interface SubtitleStyle {
  backgroundColor?: string;
  color?: string;
  fontSize?: string;
  fontFamily?: string;
  textAlign?: 'left' | 'center' | 'right';
}

export interface SubtitleFile {
  url: string;
  language: string;
  languageName: string;
  status: 'generating' | 'ready' | 'error';
  generatedAt: string;
  fileSize?: number;
  entriesCount?: number;
  style?: SubtitleStyle;
  error?: string;
}

export interface SubtitlesData {
  defaultStyle: SubtitleStyle;
  files: Record<string, SubtitleFile>;
}

export interface SubtitleEntry {
  text: string;
  startTime: number;
  endTime: number;
  confidence: number;
}

export interface SourceEpisode {
  id: string;
  sourceStoryId: string;
  name: string;
  orderIndex: number;
  startPanelId?: string;
  endPanelId?: string;
  thumbnailUrl?: string;
  masterAudioUrl?: string;
  audioStatus?: 'pending' | 'generating' | 'ready' | 'error';
  panels?: Array<{ 
    panelId: string; 
    orderIndex: number; 
    startTime?: number; 
    endTime?: number;
    confidence?: number;
    reasoning?: string;
    aiCaption?: string;
    imageUrl?: string;
    caption?: string;
    visualDescription?: string;
    motionEffects?: any[]; // Epic 3
    soundEffects?: any[];  // Epic 5
    subtitleEntries?: Record<string, SubtitleEntry[]>; // Epic 6
    // Epic 7: Video panel fields (enriched from SourcePanel DB)
    mediaType?: 'IMAGE' | 'VIDEO';
    videoUrl?: string;
    videoDuration?: number;
    firstFrameUrl?: string;
    motionPrompt?: string;
  }>;
  subtitles?: SubtitlesData; // 🆕 Epic 6
  status: 'DRAFT' | 'PUBLISHED';

  // Epic 3 V2: Video generation
  videoStatus?: string;
  videoUrl?: string;
  videoJobId?: string;
  videoError?: string;

  // Epic 5: Audio integration
  backgroundMusicId?: string;
  backgroundMusic?: AudioAsset;
  backgroundVolume?: number;

  createdAt: string;
  modifiedAt: string;
  createdBy?: string;
  modifiedBy?: string;
}

export interface ImageStyle {
  id: string;
  code: string;
  name: string;
  description?: string;
  illustrationUrl?: string;
  status: 'ACTIVE' | 'INACTIVE';
  createdAt: string;
  modifiedAt: string;
  createdBy?: string;
  modifiedBy?: string;
}

export interface SourceGlossary {
  id: string;
  sourceStoryId: string;
  originalTerm: string;
  localizedTerm: string;
  type: 'LOCATION' | 'ORGANIZATION' | 'ITEM' | 'CONCEPT' | 'OTHER';
  description?: string;
  createdAt: string;
  modifiedAt: string;
  createdBy?: string;
  modifiedBy?: string;
}

export interface SourcePanelAudio {
  id: string;
  sourceStoryId: string;
  sourcePanelId: string;
  audioUrl: string;
  duration: number;
  status: 'active' | 'inactive';
  createdAt: string;
  modifiedAt: string;
  createdBy?: string;
  modifiedBy?: string;
}

export enum AdminTaskStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  FILM_RUNNING = 'FILM_RUNNING',
  COMPLETED = 'COMPLETED',
  COMPLETED_WITH_WARNINGS = 'COMPLETED_WITH_WARNINGS',
  FAILED = 'FAILED'
}

export enum AdminTaskType {
  AUTO_GENERATE_PROCESS_CONTENT = 'AUTO_GENERATE_PROCESS_CONTENT',
  BATCH_GEN_MEDIA = 'BATCH_GEN_MEDIA'
}

export interface AdminSubtask {
  id: string;
  name: string;
  status: AdminTaskStatus;
  message?: string;
  startedAt?: string | Date;
  endedAt?: string | Date;
  result?: any;
}

export interface AdminTaskDetail<TPayload = any, TResult = any> {
  payload: TPayload;
  targetId?: string;
  progress: number;
  subtasks: AdminSubtask[];
  result?: TResult;
  error?: string;
}

export interface AdminTask<TDetail = AdminTaskDetail> {
  id: string;
  type: AdminTaskType | string;
  status: AdminTaskStatus;
  detail: TDetail;
  createdAt: string;
  modifiedAt: string;
  startedAt?: string;
  endedAt?: string;
  createdBy?: string;
  modifiedBy?: string;
}

export type AudioAssetType = 'music' | 'sound_effect';

export interface AudioAsset {
  id: string;
  name: string;
  description?: string;
  url: string;
  type: AudioAssetType;
  duration: number;
  fileSize: number;
  format: string;
  bpm?: number;
  mood: string[];
  flow?: string;
  instruments: string[];
  genre?: string;
  layering?: string;
  status: 'ACTIVE' | 'ARCHIVED' | 'PROCESSING';
  createdAt: string;
  modifiedAt: string;
  createdBy?: string;
  modifiedBy?: string;
}

export interface UpdateAudioRequest {
  name?: string;
  description?: string;
  type?: AudioAssetType;
  bpm?: number;
  mood?: string[];
  flow?: string;
  instruments?: string[];
  genre?: string;
  layering?: string;
}

export interface GenerateFromPropertiesRequest {
  name: string;
  description?: string;
  type?: AudioAssetType;
  instruments: string[];
  genre: string;
  moodAndTone: string[];
  tempo: number;
  layering: string;
  flow?: string;
  duration?: number;
  draft?: boolean;
  prompt?: string;
}

export interface SaveGeneratedAudioRequest {
  url: string;
  name: string;
  description?: string;
  type?: AudioAssetType;
  duration: number;
  fileSize: number;
  format: string;
  bpm: number;
  mood: string[];
  flow?: string;
  instruments: string[];
  genre: string;
  layering: string;
}

export interface AnalyzeFilmRequest {
  planId: string;
}
